import React, { useState } from 'react';
import classNames from 'classnames';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import DatePicker from 'components/DatePickers/DatePicker';
import SvgMembership from '../../../../static/icons/svgMembership';
import { postRewardConversion } from 'actions/Companies/Settings/postRewardConversion';
import { putRewardProgramSetting } from 'actions/Companies/Settings/PutRewardProgram';

const styles = require('./styles.pcss');

// The dropdown reflects the target program the conversion produces.
// 'points' means "convert rebate into points" (the only live option);
// 'rebate' (points -> rebate) is deferred and shown grayscaled.
type ConversionTarget = 'none' | 'points' | 'rebate';

type SelectedRewardProgram = 'none' | 'points' | 'rebate';

const REWARD_PROGRAM_LABELS: Record<SelectedRewardProgram, string> = {
  none: 'None',
  points: 'Points',
  rebate: 'Rebate',
};

type Props = {
  compact?: boolean;
  embedded?: boolean;
  organizationId?: string;
  rewardProgramSettingId?: string;
  selectedRewardProgram?: SelectedRewardProgram;
  postRewardConversion?: Function;
  putRewardProgramSetting?: Function;
  onConversionSuccess?: () => void;
};

const RewardProgramConversion: React.FC<Props> = ({
  compact = false,
  embedded = false,
  organizationId,
  rewardProgramSettingId,
  selectedRewardProgram = 'none',
  postRewardConversion,
  putRewardProgramSetting,
  onConversionSuccess,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [conversionTarget, setConversionTarget] =
    useState<ConversionTarget>('none');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [effectiveDate, setEffectiveDate] = useState<any>(null);
  const [effectiveDateError, setEffectiveDateError] = useState<string>('');

  const getInitialConversionTarget = (): ConversionTarget => {
    return selectedRewardProgram;
  };

  const resetForm = () => {
    setConversionTarget(getInitialConversionTarget());
    setEffectiveDate(null);
    setEffectiveDateError('');
  };

  const openModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSubmitting) {
      return;
    }
    setIsModalOpen(false);
    resetForm();
  };

  const validateDates = (): boolean => {
    if (!effectiveDate) {
      setEffectiveDateError('Effective date is required');
      return false;
    }
    setEffectiveDateError('');
    return true;
  };

  const submitConversion = () => {
    if (isSubmitting) {
      return;
    }

    if (
      !rewardProgramSettingId ||
      typeof putRewardProgramSetting !== 'function'
    ) {
      return;
    }

    const requiresConversion = conversionTarget === 'points';

    if (requiresConversion && !validateDates()) {
      return;
    }

    setIsSubmitting(true);

    const handleSettingUpdateSuccess = () => {
      if (
        !requiresConversion ||
        !organizationId ||
        typeof postRewardConversion !== 'function'
      ) {
        setIsSubmitting(false);
        setIsModalOpen(false);
        resetForm();
        onConversionSuccess?.();
        return;
      }

      // Window rule: start = effective date, end = today (current date).
      const effectiveMoment = moment(effectiveDate);
      const startMoment = effectiveMoment;
      const endMoment = moment();

      const payload = {
        effectiveDate: effectiveMoment.isValid()
          ? effectiveMoment.format('YYYY-MM-DD')
          : undefined,
        startDate: startMoment.format('YYYY-MM-DD'),
        endDate: endMoment.format('YYYY-MM-DD'),
      };

      postRewardConversion(
        organizationId,
        payload,
        () => {
          // Success is confirmed via the standard toast dispatched by the action.
          setIsSubmitting(false);
          setIsModalOpen(false);
          resetForm();
          // Refresh the source of truth so the displayed Rewards Program value
          // reflects the newly selected dropdown target.
          onConversionSuccess?.();
        },
        () => {
          setIsSubmitting(false);
        },
      );
    };

    putRewardProgramSetting(
      rewardProgramSettingId,
      {
        value: conversionTarget,
        organizationId,
      },
      () => {
        handleSettingUpdateSuccess();
      },
      () => {
        setIsSubmitting(false);
      },
    );
  };

  const canConvert = !isSubmitting;

  const WrapperComponent: any = embedded ? 'div' : Paper;
  const wrapperClassName = embedded
    ? `${styles.embeddedBlock}${compact ? ` ${styles.compactEmbeddedBlock}` : ''}`
    : `${styles.paper}${compact ? ` ${styles.compactPaper}` : ''}`;

  return (
    <WrapperComponent className={wrapperClassName}>
      <Table className={styles.rewardTable}>
        <TableBody>
          <TableRow className={classNames(styles.rewardTableRow)}>
            <TableCell
              key={'reward program label'}
              className={classNames(styles.rewardTableCell)}
            >
              {'Rewards Program'}
              <div className={styles.rewardDescription}>
                The company&apos;s reward program, provided by the organization.
              </div>
            </TableCell>
            <TableCell
              key={'reward program value'}
              className={classNames(styles.rewardValueCell)}
            >
              <div className={styles.rewardValueContainer}>
                <div
                  aria-label="Rewards Program"
                  className={styles.rewardValueContent}
                >
                  <SvgMembership width={20} height={20} color="#6D717A" />{' '}
                  {REWARD_PROGRAM_LABELS[selectedRewardProgram]}
                </div>
                <div
                  key={'reward program edit'}
                  className={classNames(styles.editRewardProgram)}
                  role="button"
                  tabIndex={0}
                  onClick={openModal}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      openModal();
                    }
                  }}
                >
                  Edit
                </div>
              </div>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Dialog open={isModalOpen} onClose={closeModal} fullWidth maxWidth="sm">
        <DialogTitle>
          Convert Rebate to Points
          <IconButton
            aria-label="close"
            onClick={closeModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ marginBottom: '20px' }}>
            <FormControl fullWidth size="small">
              <Select
                value={conversionTarget}
                onChange={(e) => {
                  const nextTarget = e.target.value as ConversionTarget;
                  setConversionTarget(nextTarget);
                  if (nextTarget === 'none') {
                    setEffectiveDateError('');
                  }
                }}
                disabled={isSubmitting}
                displayEmpty
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="points">Points</MenuItem>
                <MenuItem
                  value="rebate"
                  disabled
                  sx={{ color: 'text.disabled', fontStyle: 'italic' }}
                >
                  Rebate (coming soon)
                </MenuItem>
              </Select>
            </FormControl>
          </div>

          <div style={{ marginBottom: '8px' }}>
            <DatePicker
              onChange={(date: any) => {
                setEffectiveDate(date);
                setEffectiveDateError('');
              }}
              label="Effective Date *"
              date={effectiveDate}
              id="effectiveDate"
              error={effectiveDateError}
              disabled={isSubmitting || conversionTarget === 'none'}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeModal} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitConversion}
            disabled={!canConvert}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </WrapperComponent>
  );
};

function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators(
    {
      postRewardConversion,
      putRewardProgramSetting,
    },
    dispatch,
  );
}

export default connect(null, mapDispatchToProps)(RewardProgramConversion);
